import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ModelKind,
  ModelsResponse,
  CreateConnectionBody,
  CreateExitBody,
  CreateFlagBody,
  CreateItemBody,
  CreateSceneBody,
  CreateStoryBody,
  UpdateChapterBody,
  UpdateConnectionBody,
  UpdateExitBody,
  UpdateFlagBody,
  UpdateItemBody,
  UpdateSceneBody,
  UpdateStoryBody,
  UpdateSessionBody,
  CreateVersionBody,
  UpdateVersionBody,
  VersionsResponse,
  SessionResponse,
  StoryLayout,
  StoryGraph,
  AnswerCardBody,
  CreateTargetWordBody,
  CreateVocabWordBody,
  UpdateTargetWordBody,
  UpdateVocabWordBody,
} from "@storyai/shared";
import {
  chaptersApi,
  connectionsApi,
  exitsApi,
  flagsApi,
  graphApi,
  itemsApi,
  modelsApi,
  scenesApi,
  editorApi,
  sessionsApi,
  storiesApi,
  versionsApi,
  learningApi,
  targetWordsApi,
  vocabApi,
} from "./api";

// ---- Query keys -------------------------------------------------------------

export const keys = {
  publicStories: ["stories", "public"] as const,
  myStories: ["stories", "mine"] as const,
  story: (storyId: string) => ["story", storyId] as const,
  chapters: (storyId: string) => ["chapter", storyId] as const,
  chapter: (storyId: string, chapterId: string) => ["chapter", storyId, chapterId] as const,
  scenes: (storyId: string, chapterId: string) => ["scene", storyId, chapterId] as const,
  scene: (storyId: string, chapterId: string, sceneId: string) =>
    ["scene", storyId, chapterId, sceneId] as const,
  flags: (storyId: string) => ["flags", storyId] as const,
  mySessions: ["sessions", "mine"] as const,
  storySessions: (storyId: string) => ["sessions", "story", storyId] as const,
  session: (storyId: string, sessionId: string) => ["session", storyId, sessionId] as const,
  graph: (storyId: string) => ["graph", storyId] as const,
  sessionMap: (storyId: string, sessionId: string) => ["sessionMap", storyId, sessionId] as const,
  editor: (storyId: string) => ["editor", storyId] as const,
  models: ["models"] as const,
  versions: (baseId: string) => ["versions", baseId] as const,
  profile: ["profile"] as const,
};

// ---- Queries ----------------------------------------------------------------

export function usePublicStories() {
  return useQuery({ queryKey: keys.publicStories, queryFn: storiesApi.listPublic });
}

export function useMyStories() {
  return useQuery({ queryKey: keys.myStories, queryFn: storiesApi.listMine });
}

export function useStory(storyId: string) {
  return useQuery({
    queryKey: keys.story(storyId),
    queryFn: () => storiesApi.get(storyId),
    retry: false,
  });
}

export function useChapter(storyId: string, chapterId: string) {
  return useQuery({
    queryKey: keys.chapter(storyId, chapterId),
    queryFn: () => chaptersApi.get(storyId, chapterId),
    retry: false,
  });
}

export function useScene(storyId: string, chapterId: string, sceneId: string) {
  return useQuery({
    queryKey: keys.scene(storyId, chapterId, sceneId),
    queryFn: () => scenesApi.get(storyId, chapterId, sceneId),
    retry: false,
  });
}

export function useFlags(storyId: string) {
  return useQuery({
    queryKey: keys.flags(storyId),
    queryFn: () => flagsApi.list(storyId),
  });
}

// ---- Invalidation helpers ---------------------------------------------------

function useInvalidate() {
  const qc = useQueryClient();
  return {
    storyLists: () => {
      void qc.invalidateQueries({ queryKey: keys.publicStories });
      void qc.invalidateQueries({ queryKey: keys.myStories });
    },
    story: (storyId: string) => {
      void qc.invalidateQueries({ queryKey: keys.story(storyId) });
      void qc.invalidateQueries({ queryKey: keys.graph(storyId) });
    },
    /** Every chapter in a story (exits/incoming exits/flags cross chapters). */
    chapters: (storyId: string) => {
      void qc.invalidateQueries({ queryKey: keys.chapters(storyId) });
      void qc.invalidateQueries({ queryKey: keys.graph(storyId) });
    },
    chapter: (storyId: string, chapterId: string) => {
      void qc.invalidateQueries({ queryKey: keys.chapter(storyId, chapterId) });
    },
    scenes: (storyId: string, chapterId: string) => {
      void qc.invalidateQueries({ queryKey: keys.scenes(storyId, chapterId) });
      void qc.invalidateQueries({ queryKey: keys.graph(storyId) });
    },
    scene: (storyId: string, chapterId: string, sceneId: string) => {
      void qc.invalidateQueries({ queryKey: keys.scene(storyId, chapterId, sceneId) });
      void qc.invalidateQueries({ queryKey: keys.graph(storyId) });
    },
    flags: (storyId: string) => {
      void qc.invalidateQueries({ queryKey: keys.flags(storyId) });
      void qc.invalidateQueries({ queryKey: keys.graph(storyId) });
    },
    /** The story map graph; every authoring mutation touches it. */
    graph: (storyId: string) => {
      void qc.invalidateQueries({ queryKey: keys.graph(storyId) });
    },
  };
}

// ---- Story mutations --------------------------------------------------------

export function useCreateStory() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateStoryBody = {}) => storiesApi.create(body),
    onSuccess: () => inv.storyLists(),
  });
}

export function useUpdateStory(storyId: string) {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateStoryBody) => storiesApi.update(storyId, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.story(storyId), { story: data.story, isAuthor: true });
      inv.storyLists();
      inv.graph(storyId);
    },
  });
}

export function useDeleteStory() {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storyId: string) => storiesApi.remove(storyId),
    onSuccess: (_d, storyId) => {
      qc.removeQueries({ queryKey: keys.story(storyId) });
      qc.removeQueries({ queryKey: keys.graph(storyId) });
      inv.storyLists();
    },
  });
}

// ---- Chapter mutations ------------------------------------------------------

export function useCreateChapter(storyId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (title?: string) => chaptersApi.create(storyId, title ? { title } : {}),
    onSuccess: () => {
      inv.story(storyId);
      inv.chapters(storyId);
      inv.storyLists();
    },
  });
}

export function useUpdateChapter(storyId: string, chapterId: string) {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateChapterBody) => chaptersApi.update(storyId, chapterId, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.chapter(storyId, chapterId), data);
      inv.story(storyId);
      inv.chapters(storyId);
    },
  });
}

export function useDeleteChapter(storyId: string) {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chapterId: string) => chaptersApi.remove(storyId, chapterId),
    onSuccess: (_d, chapterId) => {
      qc.removeQueries({ queryKey: keys.chapter(storyId, chapterId) });
      inv.story(storyId);
      inv.chapters(storyId);
      inv.storyLists();
    },
  });
}

export function useReorderChapters(storyId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (chapterIds: string[]) => chaptersApi.reorder(storyId, chapterIds),
    onSuccess: () => {
      inv.story(storyId);
      inv.chapters(storyId);
    },
  });
}

// ---- Scene mutations --------------------------------------------------------

export function useCreateScene(storyId: string, chapterId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateSceneBody) => scenesApi.create(storyId, chapterId, body),
    onSuccess: () => {
      inv.chapter(storyId, chapterId);
      inv.scenes(storyId, chapterId);
      inv.story(storyId);
    },
  });
}

export function useUpdateScene(storyId: string, chapterId: string, sceneId: string) {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSceneBody) => scenesApi.update(storyId, chapterId, sceneId, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.scene(storyId, chapterId, sceneId), data);
      inv.chapter(storyId, chapterId);
      inv.scenes(storyId, chapterId);
    },
  });
}

export function useDeleteScene(storyId: string, chapterId: string) {
  const inv = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sceneId: string) => scenesApi.remove(storyId, chapterId, sceneId),
    onSuccess: (_d, sceneId) => {
      qc.removeQueries({ queryKey: keys.scene(storyId, chapterId, sceneId) });
      inv.chapter(storyId, chapterId);
      inv.scenes(storyId, chapterId);
      inv.story(storyId);
    },
  });
}

// ---- Item mutations ---------------------------------------------------------

export function useItemMutations(storyId: string, chapterId: string, sceneId: string) {
  const inv = useInvalidate();
  const refresh = () => {
    inv.scene(storyId, chapterId, sceneId);
    inv.chapter(storyId, chapterId);
  };
  const create = useMutation({
    mutationFn: (body: CreateItemBody) => itemsApi.create(storyId, chapterId, sceneId, body),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: UpdateItemBody }) =>
      itemsApi.update(storyId, chapterId, sceneId, itemId, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (itemId: string) => itemsApi.remove(storyId, chapterId, sceneId, itemId),
    onSuccess: refresh,
  });
  return { create, update, remove };
}

// ---- Connection mutations ---------------------------------------------------

export function useConnectionMutations(storyId: string, chapterId: string, sceneId: string) {
  const inv = useInvalidate();
  const refresh = () => inv.scenes(storyId, chapterId);
  const create = useMutation({
    mutationFn: (body: CreateConnectionBody) =>
      connectionsApi.create(storyId, chapterId, sceneId, body),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ connectionId, body }: { connectionId: string; body: UpdateConnectionBody }) =>
      connectionsApi.update(storyId, chapterId, sceneId, connectionId, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (connectionId: string) =>
      connectionsApi.remove(storyId, chapterId, sceneId, connectionId),
    onSuccess: refresh,
  });
  return { create, update, remove };
}

// ---- Flag mutations ---------------------------------------------------------

export function useFlagMutations(storyId: string) {
  const inv = useInvalidate();
  const refresh = () => {
    inv.flags(storyId);
    inv.chapters(storyId);
  };
  const create = useMutation({
    mutationFn: (body: CreateFlagBody) => flagsApi.create(storyId, body),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ flagId, body }: { flagId: string; body: UpdateFlagBody }) =>
      flagsApi.update(storyId, flagId, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (flagId: string) => flagsApi.remove(storyId, flagId),
    onSuccess: refresh,
  });
  return { create, update, remove };
}

// ---- Exit mutations ---------------------------------------------------------

export function useExitMutations(storyId: string, chapterId: string) {
  const inv = useInvalidate();
  // Incoming-exit lists on other chapters change too, so invalidate them all.
  const refresh = () => inv.chapters(storyId);
  const create = useMutation({
    mutationFn: (body: CreateExitBody) => exitsApi.create(storyId, chapterId, body),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ exitId, body }: { exitId: string; body: UpdateExitBody }) =>
      exitsApi.update(storyId, chapterId, exitId, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (exitId: string) => exitsApi.remove(storyId, chapterId, exitId),
    onSuccess: refresh,
  });
  return { create, update, remove };
}

// ---- Sessions / play --------------------------------------------------------

export function useMySessions() {
  return useQuery({ queryKey: keys.mySessions, queryFn: sessionsApi.list });
}

export function useStorySessions(storyId: string) {
  return useQuery({
    queryKey: keys.storySessions(storyId),
    queryFn: () => sessionsApi.listForStory(storyId),
    retry: false,
  });
}

export function useSession(storyId: string, sessionId: string) {
  return useQuery({
    queryKey: keys.session(storyId, sessionId),
    queryFn: () => sessionsApi.get(storyId, sessionId),
    retry: false,
    // The play page owns its state once mounted (it never invalidates this
    // key mid-play), but a fresh mount must always reload from the server so
    // returning to a session shows the latest turn.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnReconnect: false,
  });
}

/** Invalidate both session lists (Playing page + story landing page). */
export function useInvalidateSessionLists() {
  const qc = useQueryClient();
  return (storyId?: string) => {
    void qc.invalidateQueries({ queryKey: keys.mySessions });
    if (storyId) void qc.invalidateQueries({ queryKey: keys.storySessions(storyId) });
    else void qc.invalidateQueries({ queryKey: ["sessions", "story"] });
  };
}

export function useCreateSession(storyId: string) {
  const invalidate = useInvalidateSessionLists();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name?: string) => sessionsApi.create(storyId, name ? { name } : {}),
    onSuccess: (data) => {
      qc.setQueryData(keys.session(storyId, data.session.session.id), data);
      invalidate(storyId);
    },
  });
}

/** Selectable LLMs (name + description only). */
/** All selectable models, fetched once; `kind` filters in `select` so the cache is shared. */
export function useModels(kind?: ModelKind) {
  return useQuery({
    queryKey: keys.models,
    queryFn: modelsApi.list,
    staleTime: 5 * 60_000,
    select: (data: ModelsResponse): ModelsResponse =>
      kind ? { models: data.models.filter((m) => m.kind === kind) } : data,
  });
}

/** Rename and/or change the game master model. Patches the cached view. */
export function useUpdateSession(storyId: string, sessionId: string) {
  const invalidate = useInvalidateSessionLists();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSessionBody) => sessionsApi.update(storyId, sessionId, body),
    onSuccess: (data, body) => {
      qc.setQueryData<SessionResponse>(keys.session(storyId, sessionId), (prev) => {
        if (!prev) return prev;
        return {
          session: {
            ...prev.session,
            session: {
              ...prev.session.session,
              name: data.session.name,
              ...(body.modelId !== undefined ? { modelId: body.modelId } : {}),
            },
          },
        };
      });
      invalidate(storyId);
    },
  });
}

export function useRenameSession(storyId: string, sessionId: string) {
  const update = useUpdateSession(storyId, sessionId);
  return {
    ...update,
    mutateAsync: (name: string) => update.mutateAsync({ name }),
  };
}

export function useDeleteSession() {
  const invalidate = useInvalidateSessionLists();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storyId, sessionId }: { storyId: string; sessionId: string }) =>
      sessionsApi.remove(storyId, sessionId),
    onSuccess: (_d, { storyId, sessionId }) => {
      qc.removeQueries({ queryKey: keys.session(storyId, sessionId) });
      invalidate(storyId);
    },
  });
}

// ---- Story map (docs/api.md "Graph" / "Player map") --------------------------

export function useStoryGraph(storyId: string) {
  return useQuery({
    queryKey: keys.graph(storyId),
    queryFn: () => graphApi.get(storyId),
    retry: false,
  });
}

/**
 * Saves node positions without invalidating the graph (the map owns the
 * layout while mounted); just patches the cached graph's story.layout.
 */
export function useSaveLayout(storyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layout: StoryLayout) => storiesApi.update(storyId, { layout }),
    onSuccess: (data) => {
      qc.setQueryData(keys.story(storyId), { story: data.story, isAuthor: true });
      qc.setQueryData(keys.graph(storyId), (prev: { graph: StoryGraph } | undefined) =>
        prev ? { graph: { ...prev.graph, story: { ...prev.graph.story, layout: data.story.layout } } } : prev,
      );
    },
  });
}

/** Mutations the canvas itself performs (drag-to-connect, toolbar buttons). */
export function useMapMutations(storyId: string) {
  const inv = useInvalidate();
  const createChapter = useMutation({
    mutationFn: () => chaptersApi.create(storyId, {}),
    onSuccess: () => {
      inv.story(storyId);
      inv.chapters(storyId);
      inv.storyLists();
    },
  });
  const createScene = useMutation({
    mutationFn: ({ chapterId, name }: { chapterId: string; name: string }) =>
      scenesApi.create(storyId, chapterId, { name }),
    onSuccess: (_d, { chapterId }) => {
      inv.chapter(storyId, chapterId);
      inv.scenes(storyId, chapterId);
      inv.story(storyId);
    },
  });
  const createExit = useMutation({
    mutationFn: ({ chapterId, toChapterId }: { chapterId: string; toChapterId: string }) =>
      exitsApi.create(storyId, chapterId, { toChapterId, condition: "" }),
    onSuccess: () => inv.chapters(storyId),
  });
  const createConnection = useMutation({
    mutationFn: ({
      chapterId,
      sceneId,
      toSceneId,
    }: {
      chapterId: string;
      sceneId: string;
      toSceneId: string;
    }) => connectionsApi.create(storyId, chapterId, sceneId, { toSceneId, condition: null }),
    onSuccess: (_d, { chapterId }) => inv.scenes(storyId, chapterId),
  });
  return { createChapter, createScene, createExit, createConnection };
}

export function useSessionMap(storyId: string, sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.sessionMap(storyId, sessionId),
    queryFn: () => sessionsApi.map(storyId, sessionId),
    enabled,
    retry: false,
    staleTime: 0,
  });
}

// ---- AI editor (docs/api.md "Editor") -----------------------------------------

export function useEditorThread(storyId: string) {
  return useQuery({
    queryKey: keys.editor(storyId),
    queryFn: () => editorApi.thread(storyId),
    retry: false,
    staleTime: Infinity,
  });
}

export function useResetEditor(storyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => editorApi.reset(storyId),
    onSuccess: (data) => qc.setQueryData(keys.editor(storyId), data),
  });
}

/**
 * Invalidate everything the AI editor might have changed in a story: the
 * story itself, every chapter/scene/flags query, the graph, and the lists.
 */
export function useInvalidateStoryAll(storyId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.story(storyId) });
    void qc.invalidateQueries({ queryKey: keys.chapters(storyId) });
    void qc.invalidateQueries({ queryKey: ["scene", storyId] });
    void qc.invalidateQueries({ queryKey: keys.flags(storyId) });
    void qc.invalidateQueries({ queryKey: keys.graph(storyId) });
    void qc.invalidateQueries({ queryKey: keys.myStories });
  };
}

// ---- Versions (CONCEPT.md §15) --------------------------------------------------

/** Versions of a base story; polls every 2s while any transform job is running. */
export function useVersions(baseId: string, enabled = true) {
  return useQuery({
    queryKey: keys.versions(baseId),
    queryFn: () => versionsApi.list(baseId),
    enabled,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data as VersionsResponse | undefined;
      return data?.versions.some((v) => v.status === "generating") ? 2000 : false;
    },
  });
}

export function useVersionMutations(baseId: string) {
  const qc = useQueryClient();
  const inv = useInvalidate();
  const after = () => {
    void qc.invalidateQueries({ queryKey: keys.versions(baseId) });
    inv.story(baseId);
    inv.storyLists();
  };
  return {
    create: useMutation({
      mutationFn: (body: CreateVersionBody) => versionsApi.create(baseId, body),
      onSuccess: after,
    }),
    update: useMutation({
      mutationFn: (args: { versionId: string; body: UpdateVersionBody }) =>
        versionsApi.update(baseId, args.versionId, args.body),
      onSuccess: after,
    }),
    retry: useMutation({
      mutationFn: (versionId: string) => versionsApi.retry(baseId, versionId),
      onSuccess: after,
    }),
    remove: useMutation({
      mutationFn: (versionId: string) => versionsApi.remove(baseId, versionId),
      onSuccess: after,
    }),
  };
}

// ---- Learning layer ----------------------------------------------------------

/** Vocabulary words of a chapter: create / update / delete. Refreshes the chapter and the map. */
export function useVocabMutations(storyId: string, chapterId: string) {
  const inv = useInvalidate();
  const refresh = () => {
    inv.chapter(storyId, chapterId);
    inv.graph(storyId);
  };
  const create = useMutation({
    mutationFn: (body: CreateVocabWordBody) => vocabApi.create(storyId, chapterId, body),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateVocabWordBody }) => vocabApi.update(storyId, chapterId, id, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => vocabApi.remove(storyId, chapterId, id),
    onSuccess: refresh,
  });
  return { create, update, remove };
}

/** Target words of a chapter: create / update / delete. */
export function useTargetWordMutations(storyId: string, chapterId: string) {
  const inv = useInvalidate();
  const refresh = () => {
    inv.chapter(storyId, chapterId);
    inv.graph(storyId);
  };
  const create = useMutation({
    mutationFn: (body: CreateTargetWordBody) => targetWordsApi.create(storyId, chapterId, body),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTargetWordBody }) =>
      targetWordsApi.update(storyId, chapterId, id, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => targetWordsApi.remove(storyId, chapterId, id),
    onSuccess: refresh,
  });
  return { create, update, remove };
}

/** Answers a vocabulary card. The caller applies the response to local play state. */
export function useAnswerCard(storyId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AnswerCardBody) => learningApi.answerCard(storyId, sessionId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.profile });
    },
  });
}

export function useProfile() {
  return useQuery({ queryKey: keys.profile, queryFn: () => learningApi.profile(), staleTime: 10_000 });
}
